import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Charge } from '../../models/Charge'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { ChargeView } from './ChargeView'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        return new RecordNotFoundError('Could not find charge')
    }

    await RecordFactory.getInstance().fetchCharge(new Charge({ id: urlParams.id }))
    const charge = await RecordFactory.getInstance().loadCharge(urlParams.id)
    if (!charge) {
        return new RecordNotFoundError('Could not find charge')
    }

    return { charge }
}

const render = ({
    data,
    base: { logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { charge } = data

    const navigate = useNavigate()

    usePageTitle('Charge')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
    } | null>(null)

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (charge.payment?.id) {
            buttons.push({
                title: 'Copy',
                onClick: () => navigate(`/charge/${charge.payment?.id}/copy`),
            })
        }

        if (charge.markPaidDate && charge.client) {
            buttons.push({
                title: 'Receipt',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Send receipt',
                        confirmationButtonText: 'Send receipt',
                        body: 'Would you like to send the client a receipt?',
                        onConfirm: async () => {
                            setConfirmationModalData(null)

                            const result = await RecordFactory.getInstance().sendReceipt(charge)
                            if (!result.ok) {
                                logAndDisplayError(result.error)
                                return
                            }

                            navigate(`/messages/${charge.client?.id}`)
                        },
                    }),
            })
        }

        if (charge.payment) {
            buttons.push({
                title: 'Payment',
                onClick: () => navigate(`/payments/${charge.payment?.id}`),
            })
        }

        if (charge.markPaidDate && !charge.cancelledDate) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Undo',
                        body: 'This refund will be recorded for reporting purposes only. No funds will be transferred as the payment was not processed through PocketSuite.',
                        confirmationButtonText: 'Refund',
                        onConfirm: async () => {
                            setConfirmationModalData(null)
                            const result =
                                await RecordFactory.getInstance().rollbackTransaction(charge)
                            if (!result.ok) {
                                logAndDisplayError(result.error)
                                return
                            }

                            window.location.reload()
                        },
                    }),
            })
        }

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry()

    return (
        <BaseRecordView
            title="Charge"
            headerButtons={headerButtons}
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : { title: 'Home', onClick: () => navigate('/') }
            }
            tag={charge.statusTag ?? undefined}
        >
            <ChargeView charge={charge} />

            {confirmationModalData !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    size="small"
                    title={confirmationModalData.title}
                    shouldHideCancelButton
                    onConfirm={confirmationModalData.onConfirm}
                >
                    {confirmationModalData.body}
                </ConfirmationModal>
            )}
        </BaseRecordView>
    )
}

export const ChargePage = usePSComponent(render, viewWillAppear)
